
import React, { useEffect, useState } from "react";
import { SimplifiedDashboard } from "./SimplifiedDashboard";
import { EmergencyFallback } from "../EmergencyFallback";
import DashboardHeader from "./DashboardHeader";
import AnimalStats from "./AnimalStats";
import RecentActivity from "./RecentActivity";
import { useProfileData } from "../../hooks/useProfileData";
import { useAnimalsBySpecies } from "../../hooks/useAnimalsBySpecies";
import { useRecentActivity } from "../../hooks/useRecentActivity";
import { useAccount } from "@/contexts/AccountContext";
import { GlobalLoadingFallback } from "../GlobalLoadingFallback";

export const DashboardContent: React.FC = () => {
  const [renderAttempts, setRenderAttempts] = useState(0);
  const [forceSimplified, setForceSimplified] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [showFallback, setShowFallback] = useState(false);
  
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError
  } = useProfileData();
  
  const {
    currentAccount,
    isLoading: isAccountLoading,
    error: accountError
  } = useAccount();
  
  const {
    data: animalsBySpecies,
    isLoading: isAnimalsLoading,
    error: animalsError
  } = useAnimalsBySpecies();
  
  const {
    data: recentActivity = [],
    isLoading: isEventsLoading,
    error: eventsError
  } = useRecentActivity();

  // Progressive loading timeout
  useEffect(() => {
    const progressTimer = setTimeout(() => {
      if (isProfileLoading || isAccountLoading) {
        console.log('[Dashboard] Progressive loading timeout, showing simplified version');
        setForceSimplified(true);
      }
    }, 5000);

    // Fallback timer
    const fallbackTimer = setTimeout(() => {
      if (isProfileLoading) {
        console.log('[Dashboard] Fallback timer triggered');
        setShowFallback(true);
      }
    }, 8000);

    return () => {
      clearTimeout(progressTimer);
      clearTimeout(fallbackTimer);
    };
  }, [isProfileLoading, isAccountLoading]);

  // Track loading progress
  useEffect(() => {
    let step = 0;
    if (!isProfileLoading) step = 1;
    if (!isAccountLoading) step = 2;
    if (!isAnimalsLoading && !isEventsLoading) step = 3;
    setLoadingStep(step);
  }, [isProfileLoading, isAccountLoading, isAnimalsLoading, isEventsLoading]);

  useEffect(() => {
    setRenderAttempts(prev => prev + 1);
    console.log('[Dashboard] Component mounted, attempt:', renderAttempts + 1);
  }, []);

  useEffect(() => {
    console.log('[Dashboard] State update:', {
      renderAttempts,
      isProfileLoading,
      isAccountLoading,
      isAnimalsLoading,
      isEventsLoading,
      hasProfile: !!profile,
      hasCurrentAccount: !!currentAccount,
      profileError: profileError?.message,
      accountError: accountError?.message,
      forceSimplified,
      showFallback
    });
  }, [
    renderAttempts, isProfileLoading, isAccountLoading, isAnimalsLoading, 
    isEventsLoading, profile, currentAccount, profileError, accountError, 
    forceSimplified, showFallback
  ]);

  // Handle force visible from loading fallback
  const handleForceVisible = () => {
    console.log('[Dashboard] Force visible triggered');
    setForceSimplified(true);
    setShowFallback(false);
  };

  // Show loading fallback if stuck on initial load
  if (showFallback && !profile) {
    return (
      <GlobalLoadingFallback 
        isVisible={true} 
        message="Loading your dashboard..."
        onForceVisible={handleForceVisible}
        showProgress={true}
        loadingSteps={[
          "Loading user profile",
          "Loading account data", 
          "Loading dashboard content"
        ]}
        currentStep={loadingStep}
      />
    );
  }

  // Emergency fallback - if critical error or too many render attempts
  if (profileError && !profile && renderAttempts > 3) {
    console.log('[Dashboard] Using emergency fallback due to profile error');
    return <EmergencyFallback message="Unable to load your profile. Please try refreshing the page." />;
  }

  // Show simplified dashboard while loading or on errors (but allow partial content)
  if (forceSimplified || isProfileLoading || (!isProfileLoading && !profile)) {
    const reason = forceSimplified ? 'Progressive loading timeout' :
                   isProfileLoading ? 'Profile loading' :
                   !profile ? 'No profile data' : 'Unknown';
    
    console.log('[Dashboard] Using simplified dashboard:', reason);
    
    return (
      <SimplifiedDashboard 
        firstName={profile?.first_name || ''} 
        error={reason}
      />
    );
  }

  // Show partial dashboard if account is loading but profile is ready
  if (isAccountLoading || (!isAccountLoading && !currentAccount && !accountError)) {
    console.log('[Dashboard] Showing partial dashboard - profile ready, account loading');
    
    return (
      <div className="min-h-screen bg-white pb-20">
        <DashboardHeader 
          firstName={profile?.first_name || ''} 
          avatarUrl={profile?.avatar_url || undefined} 
        />

        <main className="px-4 py-6 space-y-6">
          <div className="p-8 text-center bg-gray-50 rounded-lg border">
            <div className="w-6 h-6 border-3 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">
              {accountError ? `Account error: ${accountError.message}` : 'Loading your farm data...'}
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Full dashboard mode
  console.log('[Dashboard] Rendering full dashboard');
  
  return (
    <div className="min-h-screen bg-white pb-20">
      <DashboardHeader 
        firstName={profile?.first_name || ''} 
        avatarUrl={profile?.avatar_url || undefined} 
      />

      <main className="px-4 py-6 space-y-6">
        <AnimalStats animalsBySpecies={animalsBySpecies} />
        <RecentActivity activities={recentActivity} />
      </main>
    </div>
  );
};
